import { FunctionComponent, ReactNode, useId, useRef, useState } from "react";
import { useOnMount, useOnUnmount, useOnValueChange } from "@pjs/react-utilities";
import { BusyButton, ButtonClass, focusFirstInteractiveElement, HtmlNotice, informationNotice } from "@pjs/core-ui";
import { ParseHtml } from "@pjs/security";
import { accountSettings, AccountSettingState } from "@pjs/user-domain";
import { AsyncModelStatus } from "@pjs/utilities";
import { merge, Observable } from "@pjs/observables";
import { userUiI18n } from "../i18n/UserUiI18n.const";
import { accountSettingsActions } from "../actions/AccountSettingsActions.const";
import { ISettingsViewProps } from "./interfaces/ISettingsViewProps";
import { SettingsViewNotices } from "./SettingsViewNotices.component";
import { IStateNotice } from "./interfaces/IStateNotice";
import { createAccountSettingNotices } from "./functions/CreateAccountSettingNotices.function";
import { IViewConfig } from "./interfaces/IViewConfig";
import "./styles/settings-view.css";

const noticeGenerator = {
    _observables: new Map<IViewConfig, Observable<IStateNotice>>(),
    create(activeView: IViewConfig) {
        const stateObservable = activeView.state();
        this._observables.set(activeView, stateObservable);
        return merge(...this._observables.values(), createAccountSettingNotices());
    },
    clear() {
        this._observables.clear();
    }
} as const;

export const SettingsView: FunctionComponent<ISettingsViewProps> = (props) => {
    const titleRef = useRef<HTMLHeadingElement>(null);
    const titleId = useId();
    const contentRef = useRef<HTMLDivElement>(null);

    const [noticeObservable, setNoticeObservable] = useState<Observable<IStateNotice> | null>(null);
    const [key, setKey] = useState<string | null>(null);
    const [subView, setSubView] = useState<ReactNode | null>(null);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const changesSaveLabel = hasChanges
        ? userUiI18n.getString("settings_view.a11y.save_btn_has_changes")
        : userUiI18n.getString("settings_view.a11y.save_btn_no_changes");

    useOnMount(() => {
        const sub = accountSettings.getModel().subscribe((model) => {
            setSaveInProgress(model.state.descriptor === AccountSettingState.SavePending);
            setHasChanges(accountSettings.hasChanges());
        });

        return () => {
            sub.unsubscribe();
        };
    });

    useOnValueChange(
        () => {
            const sub = props.activeView.component().subscribe((model) => {
                if (model.status === AsyncModelStatus.Pending) {
                    setKey("pending");
                    setSubView(model.pending);
                    return;
                }

                if (model.status === AsyncModelStatus.Error) {
                    setKey(props.activeView.id);
                    setSubView(model.error);
                    return;
                }

                setNoticeObservable(noticeGenerator.create(props.activeView));
                setKey(props.activeView.id);
                setSubView(model.result);
            });

            return () => {
                sub.unsubscribe();
            };
        },
        [props.activeView],
        true
    );

    useOnValueChange(() => {
        if (subView !== null) {
            focusFirstInteractiveElement(contentRef.current, titleRef.current);
        }
    }, [key]);

    useOnUnmount(() => {
        noticeGenerator.clear();
    });

    return (
        <div className="uui-settings-view">
            <SettingsViewNotices
                noticeObservable={noticeObservable}
                onNoticeDismiss={() => focusFirstInteractiveElement(contentRef.current, titleRef.current)}
            />

            <div aria-live="polite" className={hasChanges ? "" : "util-hide-element"}>
                {hasChanges && (
                    <HtmlNotice
                        type={informationNotice}
                        ariaLabel={userUiI18n.getString("settings_view.notices.a11y.has_changes")}
                        content={userUiI18n.getString("settings_view.notices.has_changes")}
                    />
                )}
            </div>

            <section className="uui-settings-view__content" aria-labelledby={titleId} key={props.activeView.id}>
                <h2 id={titleId} tabIndex={-1} className="uui-settings-view__title" data-hook="settings-view-title" ref={titleRef}>
                    {props.activeView.page.title}
                </h2>

                <ParseHtml className="uui-settings-view__description" data-hook="settings-view-description" htmlString={props.activeView.page.description} />

                <div className="uui-settings-view__view" ref={contentRef}>
                    {subView !== null && subView}
                </div>
            </section>

            <div className="uui-settings-view__footer">
                <BusyButton
                    type="submit"
                    ariaLabel={saveInProgress ? userUiI18n.getString("settings_view.a11y.save_btn_saving") : changesSaveLabel}
                    isBusy={saveInProgress}
                    disabled={!hasChanges}
                    className={ButtonClass.Contained}
                    onClick={accountSettingsActions.saveSettings}
                    dataHook="account-settings-save-button">
                    {userUiI18n.getString("settings_view.save_btn")}
                </BusyButton>
            </div>
        </div>
    );
};
